import { useMemo } from 'react'
import { addDays, isSettingLookCode, isoWeekday, type SettingLookCode } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'

/** Planning data (ARCHITECTURE 8.5, 9.8; M7), read from the device's own copy so the board, a job card and a procedure open with no network. The
 *  planner WRITES online (the commands have no projector): what the screen shows after a save is what the server confirmed. */
export interface Bilingual { fr?: string; en?: string }
export interface ParamDef { key: string; label: { fr: string; en: string }; kind: 'text' | 'number' | 'choice'; unit?: string | null; choices?: string[] }
export interface TaskTypeRow {
  id: string; code: string; label: { fr: string; en: string }; applicable_area_types: string[]; param_defs: ParamDef[]; archived_at: string | null; revision: number
  /** M16: the words the code reads (`TASK_TYPE_TAGS`, read through `hasTaskTag`); absent on a row pulled before the column existed. */
  tags?: string[]
  /** M25 (ADR-129): per choice setting, the picture the club chose for each of its words (setting key → choice text → library code), written by
   *  task_type.define@2 / update@3; absent on a row pulled before the column existed. Read only through `lookForChoice`: an entry whose setting is not a
   *  choice, whose word is no longer a choice, or whose code this build does not know is ignored, never guessed. */
  choice_looks?: Readonly<Record<string, Readonly<Record<string, string>>>>
}
export interface DocumentRow {
  id: string; kind: 'procedure' | 'manual' | 'sds' | 'label' | 'policy'
  /** M16-I2: "demander une lecture" (document.set_ask_reading@1, off by default); absent on a row pulled before the column existed (asks nothing) */
  ask_reading?: boolean
}
export interface DocumentVersionRow {
  id: string; document_id: string; version_no: number; status: 'published' | 'retired'; title: Bilingual; summary: Bilingual | null; body: Bilingual | null
  effective_from: string; published_at: string
}
export interface TemplateRow { id: string; task_type_code: string }
export interface TemplateVersionRow {
  id: string; template_id: string; version_no: number; status: 'published' | 'retired'; title: Bilingual; instructions: Bilingual | null
  procedure_document_version_id: string | null; default_duration_min: number | null; required_qualification_type_ids: string[]; default_area_ids: string[]
  default_asset_type_codes: string[]; params: Record<string, string | number>; required_crew: number | null; sequence_group: number
}
export const JOB_STATES = ['planned', 'in_progress', 'paused', 'partially_done', 'done', 'cancelled'] as const
export type JobState = (typeof JOB_STATES)[number]
export interface JobRow {
  id: string; origin: 'manual' | 'recurrence' | 'remaining_work' | 'defect'; task_type_code: string; template_version_id: string | null; recurrence_id: string | null
  occurrence_local_date: string | null; title: Bilingual | null; notes: string | null; params: Record<string, string | number>; scheduled_local_date: string
  scheduled_start_local: string | null; priority: number; sequence_group: number; required_crew: number | null; state: JobState; cancel_reason: string | null
  manually_edited: boolean; revision: number
}
export interface JobAreaRow { id: string; job_id: string; area_id: string; removed_by_command_id: string | null }
export interface AssignmentRow {
  id: string; job_id: string; person_id: string; asset_id: string | null; start_area_id: string | null; qualification_override_id: string | null; removed_by_command_id: string | null
}

export const useTaskTypes = () => useMerged<TaskTypeRow>('task_type', 'ops.task_type')
export const useDocuments = () => useMerged<DocumentRow>('document', 'core.document')
export const useDocumentVersions = () => useMerged<DocumentVersionRow>('document_version', 'core.document_version')
export const useTemplates = () => useMerged<TemplateRow>('job_template', 'ops.job_template')
export const useTemplateVersions = () => useMerged<TemplateVersionRow>('job_template_version', 'ops.job_template_version')
export const useJobs = () => useMerged<JobRow>('job', 'ops.job')
export const useJobAreas = () => useMerged<JobAreaRow>('job_area', 'ops.job_area')
export const useAssignments = () => useMerged<AssignmentRow>('assignment', 'ops.assignment')

const lang2 = (lang: string): 'fr' | 'en' => (lang.startsWith('en') ? 'en' : 'fr')
/** A bilingual text in the user's language, falling back to the other one. */
export const say = (b: Bilingual | null | undefined, lang: string): string => (b ? (b[lang2(lang)] ?? b.fr ?? b.en ?? '') : '')
export const typeLabel = (tt: Pick<TaskTypeRow, 'label'> | undefined, code: string, lang: string): string => (tt ? tt.label[lang2(lang)] : code)

export interface PlanJob extends Pending<JobRow> {
  areaIds: string[]
  /** the people on it, taken off ones left out */
  crew: Array<Pending<AssignmentRow>>
  title_text: string
}
export interface Plan {
  jobs: PlanJob[]
  byId: Map<string, PlanJob>
  /** live (not cancelled) jobs of a local day, first jobs before second jobs, then by time, then by title */
  day(date: string): PlanJob[]
  /** every live assignment of a person on a local day */
  ofPerson(personId: string, date: string): Array<{ job: PlanJob; assignment: Pending<AssignmentRow> }>
  /** the machines planned on a local day, by asset id */
  ofAsset(assetId: string, date: string): Array<{ job: PlanJob; assignment: Pending<AssignmentRow> }>
}

export function buildPlan(jobs: Array<Pending<JobRow>>, areas: Array<Pending<JobAreaRow>>, assignments: Array<Pending<AssignmentRow>>, taskTypes: TaskTypeRow[], lang: string): Plan {
  const areaOf = new Map<string, string[]>()
  for (const a of areas) if (!a.removed_by_command_id) areaOf.set(a.job_id, [...(areaOf.get(a.job_id) ?? []), a.area_id])
  const crewOf = new Map<string, Array<Pending<AssignmentRow>>>()
  for (const a of assignments) if (!a.removed_by_command_id) crewOf.set(a.job_id, [...(crewOf.get(a.job_id) ?? []), a])
  const tt = new Map(taskTypes.map((x) => [x.code, x]))
  const all: PlanJob[] = jobs.map((j) => ({ ...j, areaIds: areaOf.get(j.id) ?? [], crew: crewOf.get(j.id) ?? [], title_text: say(j.title, lang) || typeLabel(tt.get(j.task_type_code), j.task_type_code, lang) }))
  const byId = new Map(all.map((j) => [j.id, j]))
  const order = (a: PlanJob, b: PlanJob) => a.sequence_group - b.sequence_group || (a.scheduled_start_local ?? '99').localeCompare(b.scheduled_start_local ?? '99') || a.title_text.localeCompare(b.title_text, lang) || a.id.localeCompare(b.id)
  const byDate = new Map<string, PlanJob[]>()
  for (const j of all) if (j.state !== 'cancelled') byDate.set(j.scheduled_local_date, [...(byDate.get(j.scheduled_local_date) ?? []), j])
  for (const list of byDate.values()) list.sort(order)
  const lines = (match: (a: AssignmentRow) => boolean, date: string) =>
    (byDate.get(date) ?? []).flatMap((job) => job.crew.filter((a) => match(a)).map((assignment) => ({ job, assignment })))
  return {
    jobs: all, byId, day: (date) => byDate.get(date) ?? [],
    ofPerson: (personId, date) => lines((a) => a.person_id === personId, date),
    ofAsset: (assetId, date) => lines((a) => a.asset_id === assetId, date),
  }
}

/** Everything the board needs, memoised; `ready` is false until the local store has answered. */
export function usePlan(lang: string) {
  const jobs = useJobs(); const areas = useJobAreas(); const assignments = useAssignments(); const taskTypes = useTaskTypes()
  return useMemo(() => {
    const ready = !!jobs && !!areas && !!assignments && !!taskTypes
    return { ready, taskTypes: taskTypes ?? [], plan: buildPlan(jobs ?? [], areas ?? [], assignments ?? [], taskTypes ?? [], lang) }
  }, [jobs, areas, assignments, taskTypes, lang])
}

/** The Monday-based week containing a day. */
export const weekOf = (date: string): string[] => { const mon = addDays(date, -(isoWeekday(date) - 1)); return Array.from({ length: 7 }, (_, i) => addDays(mon, i)) }

/** M25 (docs/M25.md rule 9, ADR-129): the picture the club chose for a value of a setting — only for a CHOICE setting of the type whose choices hold
 *  that word, and only a code of the library; null otherwise (a word renamed or removed since, a number, a code of a later build, no picture). The code
 *  reads the picture's meaning (`lookOf`), never the word (rule 4). */
export function lookForChoice(tt: Pick<TaskTypeRow, 'param_defs' | 'choice_looks'> | undefined, key: string, value: unknown): SettingLookCode | null {
  if (!tt || typeof value !== 'string') return null
  const def = tt.param_defs.find((d) => d.key === key)
  if (!def || def.kind !== 'choice' || !def.choices?.includes(value)) return null
  const looks = tt.choice_looks, byChoice = looks && Object.hasOwn(looks, key) ? looks[key] : undefined
  const code = byChoice && Object.hasOwn(byChoice, value) ? byChoice[value] : undefined
  return isSettingLookCode(code) ? code : null
}

/** One setting of a job (a template, a rotation step) as every surface prints it. */
export interface SettingLine {
  key: string
  /** the type's label in the reader's language; the key itself for a setting the type does not declare */
  label: string
  value: string | number
  unit: string | null
  /** the picture of this value (`lookForChoice`), else null */
  look: SettingLookCode | null
  /** false for a key the type does not declare (printed last, as it is) */
  declared: boolean
}
/** THE way to read a job's settings (rule 9): in the order the task type DECLARES them (the club's order in the editor), unknown keys last in the order
 *  they came — never in the key order Postgres gives a jsonb object (shorter keys first), which printed the cutting height last on the owner's card. */
export function settingLines(params: Record<string, string | number>, tt: TaskTypeRow | undefined, lang: string): SettingLine[] {
  const l = lang2(lang), present = (k: string) => Object.hasOwn(params, k) && params[k] !== undefined && params[k] !== null
  const declared = (tt?.param_defs ?? []).filter((d) => present(d.key)).map((d): SettingLine => ({ key: d.key, label: d.label[l], value: params[d.key]!, unit: d.unit ?? null, look: lookForChoice(tt, d.key, params[d.key]), declared: true }))
  const known = new Set((tt?.param_defs ?? []).map((d) => d.key))
  const unknown = Object.keys(params).filter((k) => !known.has(k) && present(k)).map((k): SettingLine => ({ key: k, label: k, value: params[k]!, unit: null, look: null, declared: false }))
  return [...declared, ...unknown]
}

/** "Hauteur de coupe 3.5 mm · Sens de tonte …": the settings as one line of text, in the declared order (`settingLines`, same words). */
export function paramSummary(params: Record<string, string | number>, tt: TaskTypeRow | undefined, lang: string): string {
  return settingLines(params, tt, lang).map((s) => `${s.label} ${s.value}${s.unit ? ' ' + s.unit : ''}`).join(' · ')
}

/** A choice of a setting as a picker offers it (`ui/looks/LookPicker`): the club's word, and its picture or none, in the club's order. */
export interface ChoiceOption { value: string; label: string; look: SettingLookCode | null }
export function choiceOptions(tt: TaskTypeRow | undefined, key: string): ChoiceOption[] {
  const def = tt?.param_defs.find((d) => d.key === key)
  if (!def || def.kind !== 'choice') return []
  return (def.choices ?? []).map((c) => ({ value: c, label: c, look: lookForChoice(tt, key, c) }))
}

/** The version of a document that a template version points at, and the one in force now (a newer one may exist). */
export function procedureOf(versionId: string | null | undefined, versions: DocumentVersionRow[]): { used: DocumentVersionRow | undefined; latest: DocumentVersionRow | undefined } {
  const used = versions.find((v) => v.id === versionId)
  const latest = used ? versions.filter((v) => v.document_id === used.document_id).sort((a, b) => b.version_no - a.version_no)[0] : undefined
  return { used, latest }
}

/** The warnings the server computed (api.plan_overview), keyed by job. */
export interface PlanWarning { code: string; job_id: string | null; person_id: string | null; asset_id: string | null; local_date: string; detail: Record<string, unknown> }
export interface PlanOverview { from: string; to: string; warnings: PlanWarning[]; availability: Array<{ person_id: string; local_date: string; status: 'partial' | 'off' }> }
export const warningsOf = (o: PlanOverview | null, jobId: string): PlanWarning[] => (o?.warnings ?? []).filter((w) => w.job_id === jobId)
